import Card from "@/components/common/Card";
import SegmentCount from "@/components/segments/SegmentCount";
import { SEGMENT_TYPE_LABELS, SegmentType } from "@adlywise/shared";
import { groupSummaries } from "@/components/segments/conditions/conditionDisplay";
import { ACCENT_CLASS } from "@/constants/conditionCategories";

const KIND_BITS = [
  { label: "Dynamic", bit: 1 },
  { label: "Overlay", bit: 2 },
  { label: "Banner", bit: 4 },
  { label: "Static", bit: 8 },
];

const formatNumber = (n) => (Number(n) || 0).toLocaleString("en-US");

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function AudienceSummarySidebar({
  form,
  author,
  fieldOptionConfig,
  vastCount,
  vastCountLoading,
  chEstimate,
  chEstimateLoading,
  stats,
  statsLoading,
}) {
  const groups = groupSummaries(form.definition, fieldOptionConfig);
  const kinds = KIND_BITS.filter(({ bit }) => ((form.kind ?? 15) & bit) !== 0)
    .map(({ label }) => label)
    .join(", ");
  const hasStats = stats != null;
  // null = the API could not read the count (unknown, not 0).
  const reach = stats?.vasts ?? null;
  const impressions = stats?.totalImpressions ?? null;

  const typeValue = form.type ?? SegmentType.SavedSegment;
  const source = SEGMENT_TYPE_LABELS[typeValue] ?? "—";
  const authorName = form.createdBy || author || "—";

  return (
    <Card className="flex flex-col gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-(--text-muted)">
          Audience summary
        </p>
        <p className="mt-1 truncate text-base font-semibold text-(--text-primary)">
          {form.name || "Untitled audience"}
        </p>
      </div>

      <div className="rounded-(--radius-md) bg-(--surface-2) p-4">
        <p className="text-xs font-medium text-(--text-secondary)">
          Estimated unique devices
          <span className="ml-1 text-(--text-muted)">(last month)</span>
        </p>
        <p className="mt-2 text-2xl font-bold text-(--text-primary)">
          {vastCountLoading
            ? "Calculating…"
            : vastCount !== null
              ? vastCount.toLocaleString("en-US")
              : "—"}
        </p>
        {!vastCountLoading && vastCount === null && (
          <p className="mt-1 text-xs text-(--text-muted)">
            Add conditions to see the estimate
          </p>
        )}
        <div className="mt-3 border-t border-(--border-subtle) pt-3">
          <p className="text-xs font-medium text-(--text-secondary)">
            Estimated reach
            <span className="ml-1 text-(--text-muted)">
              ({chEstimate?.windowLabel ?? "last 4 full weeks + this week"})
            </span>
          </p>
          <p className="mt-1 text-lg font-semibold text-(--text-primary)">
            {chEstimateLoading
              ? "Calculating…"
              : chEstimate
                ? formatNumber(chEstimate.reach)
                : "—"}
          </p>
          {!chEstimateLoading && !chEstimate && (
            <p className="mt-1 text-xs text-(--text-muted)">
              Reach estimate unavailable right now
            </p>
          )}
        </div>
      </div>

      <dl className="flex flex-col gap-2 text-sm">
        {(hasStats || statsLoading) && (
          <>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-(--text-secondary)">Reach</dt>
              <dd className="font-medium text-(--text-primary)">
                {statsLoading ? (
                  <span className="text-(--text-muted)">…</span>
                ) : (
                  <SegmentCount
                    value={reach}
                    error={stats?.error}
                    render={formatNumber}
                  />
                )}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-(--text-secondary)">Impressions</dt>
              <dd className="font-medium text-(--text-primary)">
                {statsLoading ? (
                  <span className="text-(--text-muted)">…</span>
                ) : (
                  <SegmentCount
                    value={impressions}
                    error={stats?.error}
                    render={formatNumber}
                  />
                )}
              </dd>
            </div>
          </>
        )}
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">Source</dt>
          <dd className="font-medium text-(--text-primary)">{source}</dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">Created by</dt>
          <dd className="max-w-[60%] truncate font-medium text-(--text-primary)">
            {authorName}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">Created</dt>
          <dd className="font-medium text-(--text-primary)">
            {formatDate(form.createdAt)}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-(--text-secondary)">CPM</dt>
          <dd className="font-medium text-(--text-primary)">
            {form.cpm === "" || form.cpm === null ? "—" : form.cpm}
          </dd>
        </div>
        <div className="flex items-start justify-between gap-3">
          <dt className="shrink-0 text-(--text-secondary)">Inventory</dt>
          <dd className="text-right font-medium text-(--text-primary)">
            {kinds || "—"}
          </dd>
        </div>
      </dl>

      <div className="border-t border-(--border-subtle)" />

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-(--text-muted)">
          Conditions
        </p>
        {groups.length === 0 ? (
          <p className="text-sm text-(--text-muted)">No condition groups yet</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {groups.map(({ category, summary }) => {
              const Icon = category.icon;
              return (
                <li key={category.id} className="flex items-start gap-2.5">
                  <span
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-(--radius-sm) ${
                      ACCENT_CLASS[category.accent] ?? ACCENT_CLASS.slate
                    }`}
                  >
                    <Icon size={13} strokeWidth={2} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-(--text-primary)">
                      {category.label}
                    </p>
                    {summary && (
                      <p className="text-xs leading-snug text-(--text-secondary)">
                        {summary}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
